// src/components/manager/booking/BookingFormBuilder.jsx
import React, { useState } from 'react';
import { Plus, Minus, GripVertical, Eye } from 'lucide-react';
import { SectionBuilder } from '../../shared/forms/SectionBuilder';
import { getBookingFieldConfig } from '../../../services/bookingFieldConfiguration';
import UniversalField from '../../shared/forms/UniversalField';

export const BookingFormBuilder = ({ 
  service = {}, // Add default empty object
  onUpdate, 
  isPreview = false,
  bookData = {},
  bookId,
  onComplete
}) => {
  // Add safe defaults for all service properties
  const safeService = {
    type: 'appointment',
    customFields: [],
    ...service
  };

  const [customFields, setCustomFields] = useState(safeService.customFields || []);
  const [activeStep, setActiveStep] = useState(0);
  
  const baseConfig = getBookingFieldConfig(safeService.type);
  const [steps, setSteps] = useState(baseConfig.steps);

  const handleAddCustomField = (stepIndex) => {
    const newField = {
      id: Date.now(),
      name: `custom_${Date.now()}`,
      type: 'text',
      label: 'New Field',
      required: false,
      stepIndex
    };
    
    setCustomFields([...customFields, newField]);
  };

  const handleUpdateCustomField = (fieldId, updates) => {
    setCustomFields(customFields.map(field => 
      field.id === fieldId ? { ...field, ...updates } : field
    ));
  };

  const handleRemoveCustomField = (fieldId) => {
    setCustomFields(customFields.filter(field => field.id !== fieldId));
  };

  const renderFieldEditor = (field, isCustom = false) => {
    if (isCustom) {
      return (
        <div key={field.id} className="border border-gray-200 rounded-lg p-4">
          <div className="flex items-start gap-4">
            <div className="cursor-move text-gray-400">
              <GripVertical className="h-5 w-5" />
            </div>
            
            <div className="flex-1 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <input
                  value={field.label}
                  onChange={(e) => handleUpdateCustomField(field.id, { label: e.target.value })}
                  placeholder="Field Label"
                  className="px-3 py-2 border border-gray-300 rounded-lg"
                />
                
                <select
                  value={field.type}
                  onChange={(e) => handleUpdateCustomField(field.id, { type: e.target.value })}
                  className="px-3 py-2 border border-gray-300 rounded-lg"
                >
                  <option value="text">Text Input</option>
                  <option value="textarea">Text Area</option>
                  <option value="select">Dropdown</option>
                  <option value="toggle">Yes/No Toggle</option>
                  <option value="number">Number</option>
                  <option value="email">Email</option>
                  <option value="phone">Phone</option>
                  <option value="date">Date</option>
                </select>
              </div>
              
              <input
                value={field.placeholder || ''}
                onChange={(e) => handleUpdateCustomField(field.id, { placeholder: e.target.value })}
                placeholder="Placeholder text (optional)"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              />
              
              <div className="flex items-center gap-4">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={field.required}
                    onChange={(e) => handleUpdateCustomField(field.id, { required: e.target.checked })}
                  />
                  Required field
                </label>
                
                <button
                  onClick={() => handleRemoveCustomField(field.id)}
                  className="text-red-600 hover:text-red-700 text-sm"
                >
                  Remove
                </button>
              </div>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div key={field.name} className="border border-gray-100 rounded-lg p-4 bg-gray-50">
        <div className="flex items-center gap-2 mb-2">
          <span className="text-sm font-medium text-gray-700">{field.label}</span>
          {field.required && <span className="text-red-500 text-xs">*</span>}
        </div>
        <p className="text-xs text-gray-500">System field - cannot be modified</p>
      </div>
    );
  };

  if (isPreview) {
    return (
      <BookingFormPreview 
        steps={steps} 
        customFields={customFields} 
        service={safeService}
        bookData={bookData}
        onComplete={onComplete}
      />
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-8">
      <div className="text-center">
        <h3 className="text-2xl font-bold text-gray-900 mb-2">Customize Booking Form</h3>
        <p className="text-gray-600">Add custom fields and configure your booking process</p>
      </div>

      {/* Step Navigation */}
      <div className="flex justify-center">
        <div className="flex space-x-4">
          {steps.map((step, index) => (
            <button
              key={step.id}
              onClick={() => setActiveStep(index)}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                activeStep === index
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {step.title}
            </button>
          ))}
        </div>
      </div>

      {/* Step Content */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="mb-6">
          <h4 className="text-lg font-semibold text-gray-900">
            {steps[activeStep]?.title}
          </h4>
          <p className="text-gray-600">{steps[activeStep]?.description}</p>
        </div>

        <div className="space-y-4">
          {/* System Fields */}
          <div className="space-y-3">
            <h5 className="font-medium text-gray-700">Required Fields</h5>
            {steps[activeStep]?.fields?.map(field => renderFieldEditor(field, false)) || []}
          </div>

          {/* Custom Fields */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h5 className="font-medium text-gray-700">Custom Fields</h5>
              <button
                onClick={() => handleAddCustomField(activeStep)}
                className="flex items-center gap-2 px-3 py-1 bg-blue-600 text-white rounded text-sm hover:bg-blue-700"
              >
                <Plus className="h-4 w-4" />
                Add Field
              </button>
            </div>
            
            {customFields
              .filter(field => field.stepIndex === activeStep)
              .map(field => renderFieldEditor(field, true))}
          </div>
        </div>
      </div>

      {/* Preview Button */}
      <div className="text-center">
        <button 
          onClick={() => {/* Toggle preview mode */}}
          className="flex items-center gap-2 px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 mx-auto"
        >
          <Eye className="h-5 w-5" />
          Preview Booking Form
        </button>
      </div>
    </div>
  );
};

// Booking Form Preview Component
const BookingFormPreview = ({ 
  steps, 
  customFields, 
  service = {}, 
  bookData = {},
  onComplete 
}) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleFieldChange = (fieldName, value) => {
    setFormData(prev => ({ ...prev, [fieldName]: value }));
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    
    // Simulate booking submission
    setTimeout(() => {
      setSubmitting(false);
      onComplete?.();
    }, 2000);
  };

  const currentStepData = steps[currentStep];
  const currentStepFields = [
    ...(currentStepData?.fields || []),
    ...customFields.filter(field => field.stepIndex === currentStep)
  ];

  // Validate current step
  const isStepValid = () => {
    return currentStepFields.every(field => {
      if (!field.required) return true;
      const value = formData[field.name || field.id];
      return value && value.toString().trim().length > 0;
    });
  };

  return (
    <div className="max-w-2xl mx-auto bg-white rounded-lg shadow-lg">
      {/* Progress Bar */}
      <div className="p-6 border-b border-gray-200">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold">
            Book {service.type === 'appointment' ? 'an Appointment' : 
                  service.type === 'consultation' ? 'a Consultation' :
                  service.type === 'class' ? 'a Class' :
                  service.type === 'facility' ? 'a Facility' : 
                  'a Service'}
          </h3>
          <span className="text-sm text-gray-500">
            Step {currentStep + 1} of {steps.length}
          </span>
        </div>
        
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div 
            className="bg-blue-600 h-2 rounded-full transition-all duration-300"
            style={{ width: `${((currentStep + 1) / steps.length) * 100}%` }}
          />
        </div>
      </div>

      {/* Form Content */}
      <div className="p-6">
        <div className="mb-6">
          <h4 className="text-xl font-semibold text-gray-900 mb-2">
            {currentStepData?.title}
          </h4>
          <p className="text-gray-600">{currentStepData?.description}</p>
        </div>

        <div className="space-y-6">
          {currentStepFields.map(field => (
            <UniversalField
              key={field.name || field.id}
              field={field}
              value={formData[field.name || field.id]}
              onChange={(value) => handleFieldChange(field.name || field.id, value)}
            />
          ))}
        </div>
      </div>

      {/* Navigation */}
      <div className="p-6 border-t border-gray-200 flex justify-between">
        <button
          onClick={handlePrevious}
          disabled={currentStep === 0}
          className="px-4 py-2 text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed hover:text-gray-800"
        >
          Previous
        </button>
        
        {currentStep < steps.length - 1 ? (
          <button
            onClick={handleNext}
            disabled={!isStepValid()}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Next Step
          </button>
        ) : (
          <button 
            onClick={handleSubmit}
            disabled={!isStepValid() || submitting}
            className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {submitting ? (
              <>
                <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
                Booking...
              </>
            ) : (
              'Complete Booking'
            )}
          </button>
        )}
      </div>
    </div>
  );
};